import type { ProfileData, ProfileExperience, ProfileEducation } from "@/features/candidate/lib/types";

// ---------------------------------------------------------------------------
// Curated company lists
// ---------------------------------------------------------------------------

const BIG_TECH = new Set([
  "apple", "microsoft", "google", "amazon", "meta", "tesla", "alibaba",
  "tencent", "huawei", "salesforce", "adobe", "nvidia", "twitter", "bytedance",
  "uber", "airbnb", "netflix", "spotify", "zoom", "palantir", "snowflake",
  "shopify", "datadog", "square", "linkedin", "atlassian", "cloudflare",
  "pinterest", "snap", "paypal", "activision", "workday", "servicenow",
  "doordash", "roblox", "reddit",
]);

const FAANG = new Set(["meta", "facebook", "netflix", "amazon", "apple", "google", "alphabet"]);

const BIG_4 = new Set(["kpmg", "ey", "pwc", "deloitte"]);

const TOP_INVESTMENT_BANKS = new Set([
  "goldman sachs", "jpmorgan chase", "morgan stanley",
  "bank of america merrill lynch", "citi", "credit suisse",
  "barclays", "deutsche bank", "ubs", "hsbc",
]);

const TOP_CONSULTING_FIRMS = new Set([
  "mckinsey & company", "boston consulting group", "bain & company",
  "accenture", "oliver wyman", "roland berger", "kearney", "l.e.k. consulting",
]);

// ---------------------------------------------------------------------------
// Curated university lists
// ---------------------------------------------------------------------------

const TOP_10_US_UNI = new Set([
  "princeton university", "massachusetts institute of technology",
  "harvard university", "stanford university", "yale university",
  "university of pennsylvania", "caltech", "duke university",
  "brown university", "johns hopkins university",
]);

const TOP_20_US_UNI = new Set([
  ...TOP_10_US_UNI,
  "northwestern university", "columbia university", "cornell university",
  "university of chicago", "university of california, berkeley",
  "rice university", "dartmouth college", "vanderbilt university",
  "university of notre dame", "ucla",
]);

const TOP_50_US_UNI = new Set([
  ...TOP_20_US_UNI,
  "university of michigan - ann arbor", "georgetown university",
  "university of north carolina at chapel hill", "carnegie mellon university",
  "emory university", "university of virginia",
  "washington university in st. louis", "university of california, davis",
  "uc san diego", "university of florida", "university of southern california",
  "university of texas at austin", "georgia institute of technology",
  "uc irvine", "new york university", "uc santa barbara",
  "university of illinois urbana - champaign",
  "university of wisconsin - madison", "boston college",
  "rutgers university - new brunswick", "tufts university",
  "university of washington", "boston university", "the ohio state university",
  "purdue university - main campus", "university of maryland, college park",
  "lehigh university", "texas a&m university", "university of georgia",
  "university of rochester",
]);

const TOP_100_GLOBAL_UNI = new Set([
  "university of oxford", "stanford university",
  "massachusetts institute of technology", "harvard university",
  "university of cambridge", "princeton university", "caltech",
  "imperial college london", "university of california, berkeley",
  "yale university", "tsinghua university", "the university of chicago",
  "peking university", "johns hopkins university",
  "university of pennsylvania", "columbia university",
  "national university of singapore", "cornell university",
  "university of toronto", "university of michigan - ann arbor",
  "carnegie mellon university", "university of washington", "duke university",
  "new york university", "northwestern university", "the university of tokyo",
  "university of edinburgh", "technical university of munich",
  "uc san diego", "university of hong kong",
  "georgia institute of technology", "university of melbourne",
  "king's college london", "university of british columbia",
  "university of illinois urbana - champaign",
  "shanghai jiao tong university", "fudan university",
  "london school of economics and political science", "heidelberg university",
  "mcgill university", "university of manchester",
  "university of texas at austin", "university of california, davis",
  "university of sydney", "university of amsterdam",
  "seoul national university", "university of wisconsin - madison",
  "brown university", "australian national university",
  "washington university in st. louis", "uc santa barbara",
  "university of north carolina at chapel hill", "nanjing university",
  "university of southern california", "boston university",
  "university of bristol", "city university of hong kong",
  "university of minnesota", "university of glasgow",
  "humboldt university of berlin", "rwth aachen university",
  "university of bonn", "uc irvine", "vanderbilt university",
  "ohio state university", "ucla", "eth zürich", "ucl", "ku leuven", "kaist", "unsw",
]);

const HBCU = new Set([
  "morgan state university", "alabama a&m university",
  "alabama state university", "albany state university",
  "alcorn state university", "allen university",
  "university of arkansas at pine bluff", "benedict college",
  "bethune - cookman university", "bowie state university",
  "central state university", "cheyney university of pennsylvania",
  "claflin university", "clark atlanta university",
  "coppin state university", "delaware state university",
  "dillard university", "edward waters university",
  "elizabeth city state university", "fayetteville state university",
  "fisk university", "florida a&m university",
  "florida memorial university", "fort valley state university",
  "grambling state university", "hampton university",
  "harris - stowe state university", "howard university",
  "huston - tillotson university", "jackson state university",
  "jarvis christian college", "johnson c. smith university",
  "kentucky state university", "lane college", "langston university",
  "lemoyne - owen college", "the lincoln university",
  "livingstone college", "meharry medical college", "miles college",
  "mississippi valley state university", "morehouse college",
  "morehouse school of medicine", "morris college",
  "norfolk state university", "north carolina a&t state university",
  "north carolina central university", "oakwood university",
  "paine college", "paul quinn college", "philander smith college",
  "prairie view a&m university", "rust college",
  "savannah state university", "shaw university",
  "south carolina state university", "southern university at new orleans",
  "southern university and a&m college", "spelman college",
  "st. augustine's university", "stillman college", "talladega college",
  "tennessee state university", "texas college", "texas southern university",
  "tougaloo college", "tuskegee university", "virginia state university",
  "virginia union university", "voorhees university",
  "west virginia state university", "wilberforce university", "wiley university",
]);

const TOP_MBA_PROGRAMS = new Set([
  "harvard business school",
  "stanford university", "stanford graduate school of business",
  "the wharton school", "wharton",
  "mit sloan", "sloan school of management",
  "university of chicago booth", "chicago booth",
  "columbia business school",
  "northwestern university kellogg", "kellogg school of management",
  "university of california berkeley haas", "berkeley haas",
  "insead", "london business school",
  "mccombs school of business", "texas mccombs",
  "tuck school of business", "dartmouth tuck",
  "ross school of business", "michigan ross",
  "fuqua school of business", "duke fuqua",
  "darden school of business", "virginia darden",
  "yale school of management",
  "anderson school of management", "ucla anderson",
  "stern school of business", "nyu stern",
  "tepper school of business", "carnegie mellon tepper",
  "cornell johnson", "johnson graduate school",
]);

// ---------------------------------------------------------------------------
// Keyword lists
// ---------------------------------------------------------------------------

const WEB_3_KEYWORDS = [
  "web3", "defi", "blockchain", "smart contract", "cryptocurrency", "dapp",
  "nft", "dao", "ethereum", "solidity", "polkadot", "solana", "layer 2",
  "metaverse", "tokenomics", "crypto", "decentralized exchange",
  "yield farming", "staking", "governance token", "digital assets",
  "blockchain developer", "crypto wallet", "web 3.0", "cross-chain",
];

const FRONTEND_TECHS = [
  "react", "vue", "angular", "svelte", "next.js", "nuxt", "gatsby",
  "tailwind", "css", "html", "javascript", "typescript", "webpack", "vite",
];

const BACKEND_TECHS = [
  "node.js", "express", "django", "flask", "fastapi", "spring",
  "ruby on rails", "golang", "rust", "java", "python", "c#", ".net",
  "postgresql", "mysql", "mongodb", "redis", "graphql", "grpc",
];

const PATENT_KEYWORDS = ["patent pending", "patent holder", "patent", "patented", "us patent"];

// ---------------------------------------------------------------------------
// Highlight type enum + frames
// ---------------------------------------------------------------------------

export enum HighlightType {
  BIG_TECH = "big_tech",
  EX_FAANG = "ex_faang",
  BIG_4 = "big_4_experience",
  TOP_INVESTMENT_BANK = "top_investment_bank",
  TOP_CONSULTING_FIRM = "top_consulting_firm",
  BOARD_MEMBER = "board_member",
  PROMOTED_TWICE = "promoted_twice",
  LONG_TENURE = "long_tenure",
  PHD_HOLDER = "phd_holder",
  MEDICAL_DOCTOR = "medical_doctor",
  PATENT = "patent",
  FULLSTACK = "fullstack",
  WEB3 = "web3_experience",
  TOP_10_US_UNI = "top_10_us_uni",
  TOP_20_US_UNI = "top_20_us_uni",
  TOP_50_US_UNI = "top_50_us_uni",
  TOP_100_GLOBAL_UNI = "top_100_global_uni",
  HBCU = "hbcu",
  TOP_MBA = "top_mba",
  // Competitions & awards
  NO_LINKEDIN = "no_linkedin",
  ICPC_WORLD_FINALIST = "icpc_world_finalist",
  IOI_MEDALIST = "ioi_medalist",
  IMO_MEDALIST = "imo_medalist",
  IPHO_MEDALIST = "ipho_medalist",
  META_HACKER_CUP = "meta_hacker_cup",
  RHODES_SCHOLAR = "rhodes_scholar",
  EXCEPTIONAL_ABILITY_VISA = "exceptional_ability_visa",
  // Career
  PROMOTED = "promoted",
  FAST_CAREER_GROWTH_ENGINEERING = "fast_career_growth_engineering",
  VC_BACKED_FOUNDER = "vc_backed_founder",
  VP_PLUS_PE_BACKED = "vp_plus_pe_backed",
  VP_PLUS_VC_BACKED = "vp_plus_vc_backed",
  TOP_VC_BACKED_EXPERIENCE = "top_vc_backed_experience",
  EARLY_TEAM_FOUNDING = "early_team_founding",
  // Diversity
  GENDER_DIVERSITY = "gender_diversity",
  BLACK_OR_AFRICAN_AMERICAN = "black_or_african_american",
  HISPANIC_OR_LATINX = "hispanic_or_latinx",
  // Security clearance
  SECRET_CLEARANCE = "secret_clearance",
  TOP_SECRET_CLEARANCE = "top_secret_clearance",
  TOP_SECRET_SCI_CLEARANCE = "top_secret_sci_clearance",
  TS_FULL_SCOPE_POLY_CLEARANCE = "ts_full_scope_poly_clearance",
  TS_LIFESTYLE_POLY_CLEARANCE = "ts_lifestyle_poly_clearance",
  TS_CI_POLY_CLEARANCE = "ts_ci_poly_clearance",
  DOE_Q_CLEARANCE = "doe_q_clearance",
  // Experiences
  D1_ATHLETE = "d1_athlete",
  MILITARY_VETERAN = "military_veteran",
  HACKATHON_EXPERIENCE = "hackathon_experience",
  CHARTER_SCHOOL_ALUMNI = "charter_school_alumni",
  STARTUP_EXPERIENCE = "startup_experience",
  GENERATIVE_AI_EXPERIENCE = "generative_ai_experience",
  TRAVEL_NURSE = "travel_nurse",
  HEALTHCARE_IMPLEMENTATION_ENGINEERS = "healthcare_implementation_engineers",
  // Sales
  SALES_MEDDPICC = "sales_meddpicc",
  SALES_SMB = "sales_smb",
  SALES_MID_MARKET = "sales_mid_market",
  SALES_ENTERPRISE = "sales_enterprise",
  PRESIDENTS_CLUB = "presidents_club",
  WORKED_FOR_SALES_LEADERS = "worked_for_sales_leaders",
  QUOTA_ATTAINMENT = "quota_attainment",
  B2B_SAAS_SALES = "b2b_saas_sales",
}

export interface HighlightResult {
  type: HighlightType;
  title: string;
  description: string;
  icon: string;
  color: string;
  gradientRgb: string;
  /** Light-mode legible variants; consumers fall back to `color`/`gradientRgb`. */
  lightColor?: string;
  lightGradientRgb?: string;
}

interface HighlightFrame {
  icon: string;
  color: string;
  gradientRgb: string;
  /**
   * Light-mode equivalents for the dark-tuned pastel. The `color`/`gradientRgb`
   * are pale tints chosen to read on the dark surface and wash out on white, so
   * each frame carries a darker, same-hue light variant.
   */
  lightColor?: string;
  lightGradientRgb?: string;
  label: string;
  excludes?: HighlightType[];
}

export const HIGHLIGHT_FRAMES: Record<HighlightType, HighlightFrame> = {
  [HighlightType.BIG_TECH]: {
    icon: "IconStar",
    color: "#deff72",
    gradientRgb: "222,255,114",
    lightColor: "#65a30d",
    lightGradientRgb: "101,163,13",
    label: "Big Tech",
  },
  [HighlightType.EX_FAANG]: {
    icon: "IconMagicWand",
    color: "#f9a8d4",
    gradientRgb: "249,168,212",
    lightColor: "#db2777",
    lightGradientRgb: "219,39,119",
    label: "FAANG",
    excludes: [HighlightType.BIG_TECH],
  },
  [HighlightType.BIG_4]: {
    icon: "IconSquareBehindSquare4",
    color: "#6ee7b7",
    gradientRgb: "110,231,183",
    lightColor: "#047857",
    lightGradientRgb: "4,120,87",
    label: "Big 4",
  },
  [HighlightType.TOP_INVESTMENT_BANK]: {
    icon: "IconBank",
    color: "#fdba74",
    gradientRgb: "253,186,116",
    lightColor: "#ea580c",
    lightGradientRgb: "234,88,12",
    label: "Top Investment Bank",
  },
  [HighlightType.TOP_CONSULTING_FIRM]: {
    icon: "IconBuildings",
    color: "#c4b5fd",
    gradientRgb: "196,181,253",
    lightColor: "#7c3aed",
    lightGradientRgb: "124,58,237",
    label: "Top Consulting Firm",
  },
  [HighlightType.BOARD_MEMBER]: {
    icon: "IconPeople2",
    color: "#93c5fd",
    gradientRgb: "147,197,253",
    lightColor: "#2563eb",
    lightGradientRgb: "37,99,235",
    label: "Board Member",
  },
  [HighlightType.PROMOTED_TWICE]: {
    icon: "IconTrending4",
    color: "#86efac",
    gradientRgb: "134,239,172",
    lightColor: "#16a34a",
    lightGradientRgb: "22,163,74",
    label: "Promoted Twice",
  },
  [HighlightType.LONG_TENURE]: {
    icon: "IconHistory",
    color: "#fca5a5",
    gradientRgb: "252,165,165",
    lightColor: "#dc2626",
    lightGradientRgb: "220,38,38",
    label: "Long Tenure",
  },
  [HighlightType.PHD_HOLDER]: {
    icon: "IconGraduateCap",
    color: "#f0abfc",
    gradientRgb: "240,171,252",
    lightColor: "#c026d3",
    lightGradientRgb: "192,38,211",
    label: "PhD Holder",
  },
  [HighlightType.MEDICAL_DOCTOR]: {
    icon: "IconStethoscope",
    color: "#fca5a5",
    gradientRgb: "252,165,165",
    lightColor: "#dc2626",
    lightGradientRgb: "220,38,38",
    label: "Medical Doctor",
  },
  [HighlightType.PATENT]: {
    icon: "IconFileBend",
    color: "#a5b4fc",
    gradientRgb: "165,180,252",
    lightColor: "#4f46e5",
    lightGradientRgb: "79,70,229",
    label: "Patent Holder",
  },
  [HighlightType.FULLSTACK]: {
    icon: "IconCode",
    color: "#d1d5db",
    gradientRgb: "209,213,219",
    lightColor: "#4b5563",
    lightGradientRgb: "75,85,99",
    label: "Fullstack",
  },
  [HighlightType.WEB3]: {
    icon: "IconImagineAi",
    color: "#67e8f9",
    gradientRgb: "103,232,249",
    lightColor: "#0e7490",
    lightGradientRgb: "14,116,144",
    label: "Web3",
  },
  [HighlightType.TOP_10_US_UNI]: {
    icon: "IconSchool",
    color: "#fbbf24",
    gradientRgb: "251,191,36",
    lightColor: "#b45309",
    lightGradientRgb: "180,83,9",
    label: "Top 10 US Uni",
    excludes: [HighlightType.TOP_20_US_UNI, HighlightType.TOP_50_US_UNI, HighlightType.TOP_100_GLOBAL_UNI],
  },
  [HighlightType.TOP_20_US_UNI]: {
    icon: "IconSchool",
    color: "#d1d5db",
    gradientRgb: "209,213,219",
    lightColor: "#4b5563",
    lightGradientRgb: "75,85,99",
    label: "Top 20 US Uni",
    excludes: [HighlightType.TOP_50_US_UNI, HighlightType.TOP_100_GLOBAL_UNI],
  },
  [HighlightType.TOP_50_US_UNI]: {
    icon: "IconBooks",
    color: "#d1d5db",
    gradientRgb: "209,213,219",
    lightColor: "#4b5563",
    lightGradientRgb: "75,85,99",
    label: "Top 50 US Uni",
    excludes: [HighlightType.TOP_100_GLOBAL_UNI],
  },
  [HighlightType.TOP_100_GLOBAL_UNI]: {
    icon: "IconGlobe",
    color: "#fbbf24",
    gradientRgb: "251,191,36",
    lightColor: "#b45309",
    lightGradientRgb: "180,83,9",
    label: "Top 100 Global Uni",
  },
  [HighlightType.HBCU]: {
    icon: "IconBooks",
    color: "#93c5fd",
    gradientRgb: "147,197,253",
    lightColor: "#2563eb",
    lightGradientRgb: "37,99,235",
    label: "HBCU",
  },
  [HighlightType.TOP_MBA]: {
    icon: "IconGraduateCap",
    color: "#6ee7b7",
    gradientRgb: "110,231,183",
    lightColor: "#047857",
    lightGradientRgb: "4,120,87",
    label: "Top MBA",
  },

  // ----- Competitions & awards -----
  [HighlightType.NO_LINKEDIN]: {
    icon: "IconMagicWand", color: "#d1d5db", gradientRgb: "209,213,219",
    lightColor: "#4b5563", lightGradientRgb: "75,85,99",
    label: "No Social",
  },
  [HighlightType.ICPC_WORLD_FINALIST]: {
    icon: "IconStar", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "ICPC World Finalist",
  },
  [HighlightType.IOI_MEDALIST]: {
    icon: "IconStar", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "IOI Medalist",
  },
  [HighlightType.IMO_MEDALIST]: {
    icon: "IconStar", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "IMO Medalist",
  },
  [HighlightType.IPHO_MEDALIST]: {
    icon: "IconStar", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "IPhO Medalist",
  },
  [HighlightType.META_HACKER_CUP]: {
    icon: "IconCode", color: "#67e8f9", gradientRgb: "103,232,249",
    lightColor: "#0e7490", lightGradientRgb: "14,116,144",
    label: "Meta Hacker Cup",
  },
  [HighlightType.RHODES_SCHOLAR]: {
    icon: "IconGraduateCap", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "Rhodes Scholar",
  },
  [HighlightType.EXCEPTIONAL_ABILITY_VISA]: {
    icon: "IconStar", color: "#f0abfc", gradientRgb: "240,171,252",
    lightColor: "#c026d3", lightGradientRgb: "192,38,211",
    label: "O-1 / EB-1 Visa",
  },

  // ----- Career -----
  [HighlightType.PROMOTED]: {
    icon: "IconTrending4", color: "#86efac", gradientRgb: "134,239,172",
    lightColor: "#16a34a", lightGradientRgb: "22,163,74",
    label: "Promoted",
  },
  [HighlightType.FAST_CAREER_GROWTH_ENGINEERING]: {
    icon: "IconTrending4", color: "#86efac", gradientRgb: "134,239,172",
    lightColor: "#16a34a", lightGradientRgb: "22,163,74",
    label: "Fast Career Growth",
  },
  [HighlightType.VC_BACKED_FOUNDER]: {
    icon: "IconMagicWand", color: "#deff72", gradientRgb: "222,255,114",
    lightColor: "#65a30d", lightGradientRgb: "101,163,13",
    label: "VC-Backed Founder",
  },
  [HighlightType.VP_PLUS_PE_BACKED]: {
    icon: "IconBank", color: "#fdba74", gradientRgb: "253,186,116",
    lightColor: "#ea580c", lightGradientRgb: "234,88,12",
    label: "VP+ at PE-Backed",
  },
  [HighlightType.VP_PLUS_VC_BACKED]: {
    icon: "IconBank", color: "#deff72", gradientRgb: "222,255,114",
    lightColor: "#65a30d", lightGradientRgb: "101,163,13",
    label: "VP+ at VC-Backed",
  },
  [HighlightType.TOP_VC_BACKED_EXPERIENCE]: {
    icon: "IconMagicWand", color: "#deff72", gradientRgb: "222,255,114",
    lightColor: "#65a30d", lightGradientRgb: "101,163,13",
    label: "Top VC-Backed",
  },
  [HighlightType.EARLY_TEAM_FOUNDING]: {
    icon: "IconPeople2", color: "#fdba74", gradientRgb: "253,186,116",
    lightColor: "#ea580c", lightGradientRgb: "234,88,12",
    label: "Early / Founding Team",
  },

  // ----- Diversity -----
  [HighlightType.GENDER_DIVERSITY]: {
    icon: "IconPeople2", color: "#f9a8d4", gradientRgb: "249,168,212",
    lightColor: "#db2777", lightGradientRgb: "219,39,119",
    label: "Gender Diversity",
  },
  [HighlightType.BLACK_OR_AFRICAN_AMERICAN]: {
    icon: "IconPeople2", color: "#93c5fd", gradientRgb: "147,197,253",
    lightColor: "#2563eb", lightGradientRgb: "37,99,235",
    label: "Black or African American",
  },
  [HighlightType.HISPANIC_OR_LATINX]: {
    icon: "IconPeople2", color: "#fdba74", gradientRgb: "253,186,116",
    lightColor: "#ea580c", lightGradientRgb: "234,88,12",
    label: "Hispanic or Latinx",
  },

  // ----- Security clearance -----
  [HighlightType.SECRET_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "Secret Clearance",
  },
  [HighlightType.TOP_SECRET_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "Top Secret",
  },
  [HighlightType.TOP_SECRET_SCI_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "Top Secret SCI",
  },
  [HighlightType.TS_FULL_SCOPE_POLY_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "TS Full Scope Poly",
  },
  [HighlightType.TS_LIFESTYLE_POLY_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "TS Lifestyle Poly",
  },
  [HighlightType.TS_CI_POLY_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "TS CI Poly",
  },
  [HighlightType.DOE_Q_CLEARANCE]: {
    icon: "IconBank", color: "#a5b4fc", gradientRgb: "165,180,252",
    lightColor: "#4f46e5", lightGradientRgb: "79,70,229",
    label: "DoE Q Clearance",
  },

  // ----- Experiences -----
  [HighlightType.D1_ATHLETE]: {
    icon: "IconStar", color: "#86efac", gradientRgb: "134,239,172",
    lightColor: "#16a34a", lightGradientRgb: "22,163,74",
    label: "D1 Athlete",
  },
  [HighlightType.MILITARY_VETERAN]: {
    icon: "IconStar", color: "#fca5a5", gradientRgb: "252,165,165",
    lightColor: "#dc2626", lightGradientRgb: "220,38,38",
    label: "Military Veteran",
  },
  [HighlightType.HACKATHON_EXPERIENCE]: {
    icon: "IconCode", color: "#67e8f9", gradientRgb: "103,232,249",
    lightColor: "#0e7490", lightGradientRgb: "14,116,144",
    label: "Hackathon Experience",
  },
  [HighlightType.CHARTER_SCHOOL_ALUMNI]: {
    icon: "IconSchool", color: "#93c5fd", gradientRgb: "147,197,253",
    lightColor: "#2563eb", lightGradientRgb: "37,99,235",
    label: "Charter School Alumni",
  },
  [HighlightType.STARTUP_EXPERIENCE]: {
    icon: "IconMagicWand", color: "#deff72", gradientRgb: "222,255,114",
    lightColor: "#65a30d", lightGradientRgb: "101,163,13",
    label: "Startup Experience",
  },
  [HighlightType.GENERATIVE_AI_EXPERIENCE]: {
    icon: "IconImagineAi", color: "#67e8f9", gradientRgb: "103,232,249",
    lightColor: "#0e7490", lightGradientRgb: "14,116,144",
    label: "Generative AI",
  },
  [HighlightType.TRAVEL_NURSE]: {
    icon: "IconGlobe", color: "#fdba74", gradientRgb: "253,186,116",
    lightColor: "#ea580c", lightGradientRgb: "234,88,12",
    label: "Travel Nurse",
  },
  [HighlightType.HEALTHCARE_IMPLEMENTATION_ENGINEERS]: {
    icon: "IconCode", color: "#6ee7b7", gradientRgb: "110,231,183",
    lightColor: "#047857", lightGradientRgb: "4,120,87",
    label: "Healthcare Implementation",
  },

  // ----- Sales -----
  [HighlightType.SALES_MEDDPICC]: {
    icon: "IconTrending4", color: "#c4b5fd", gradientRgb: "196,181,253",
    lightColor: "#7c3aed", lightGradientRgb: "124,58,237",
    label: "MEDDPICC",
  },
  [HighlightType.SALES_SMB]: {
    icon: "IconTrending4", color: "#93c5fd", gradientRgb: "147,197,253",
    lightColor: "#2563eb", lightGradientRgb: "37,99,235",
    label: "SMB Sales",
  },
  [HighlightType.SALES_MID_MARKET]: {
    icon: "IconTrending4", color: "#93c5fd", gradientRgb: "147,197,253",
    lightColor: "#2563eb", lightGradientRgb: "37,99,235",
    label: "Mid-Market Sales",
  },
  [HighlightType.SALES_ENTERPRISE]: {
    icon: "IconTrending4", color: "#93c5fd", gradientRgb: "147,197,253",
    lightColor: "#2563eb", lightGradientRgb: "37,99,235",
    label: "Enterprise Sales",
  },
  [HighlightType.PRESIDENTS_CLUB]: {
    icon: "IconStar", color: "#fbbf24", gradientRgb: "251,191,36",
    lightColor: "#b45309", lightGradientRgb: "180,83,9",
    label: "President's Club",
  },
  [HighlightType.WORKED_FOR_SALES_LEADERS]: {
    icon: "IconPeople2", color: "#c4b5fd", gradientRgb: "196,181,253",
    lightColor: "#7c3aed", lightGradientRgb: "124,58,237",
    label: "Worked for Sales Leaders",
  },
  [HighlightType.QUOTA_ATTAINMENT]: {
    icon: "IconTrending4", color: "#86efac", gradientRgb: "134,239,172",
    lightColor: "#16a34a", lightGradientRgb: "22,163,74",
    label: "Quota Attainment",
  },
  [HighlightType.B2B_SAAS_SALES]: {
    icon: "IconBuildings", color: "#c4b5fd", gradientRgb: "196,181,253",
    lightColor: "#7c3aed", lightGradientRgb: "124,58,237",
    label: "B2B SaaS Sales",
  },
};

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const norm = (s?: string | null): string => (s ?? "").toLowerCase().trim();

function matchesSet(company: string, set: Set<string>): boolean {
  const low = norm(company);
  if (set.has(low)) return true;
  for (const entry of set) {
    const escaped = entry.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    if (new RegExp(`\\b${escaped}\\b`).test(low)) return true;
  }
  return false;
}

function schoolMatchesSet(school: string, set: Set<string>): boolean {
  const low = norm(school);
  if (set.has(low)) return true;
  for (const entry of set) {
    const words = entry.split(/\s+/);
    if (words.every((w) => low.includes(w))) return true;
  }
  return false;
}

function monthsBetween(start?: string, end?: string): number {
  if (!start) return 0;
  const s = new Date(start);
  const e = end ? new Date(end) : new Date();
  return Math.max(0, Math.round((e.getTime() - s.getTime()) / (1000 * 60 * 60 * 24 * 30.44)));
}

function formatMonthsDuration(months: number): string {
  const years = Math.floor(months / 12);
  const rem = months % 12;
  const parts: string[] = [];
  if (years > 0) parts.push(`${years} ${years === 1 ? "year" : "years"}`);
  if (rem > 0) parts.push(`${rem} ${rem === 1 ? "month" : "months"}`);
  return parts.join(" and ") || "0 months";
}

// ---------------------------------------------------------------------------
// Detectors
// ---------------------------------------------------------------------------

type DetectorFn = (profile: ProfileData) => { title: string; description: string } | null;

// Detectors drive profile-card badges. Highlights that only make sense as
// server-side filters (e.g. clearances, competitions, diversity signals)
// can be omitted — they still show up in the filter picker because
// HIGHLIGHT_FRAMES covers them.
const detectors: Partial<Record<HighlightType, DetectorFn>> = {
  [HighlightType.BIG_TECH]: (p) => {
    const exp = p.experience.find((e) => matchesSet(e.company, BIG_TECH));
    if (!exp) return null;
    const verb = exp.is_current ? "Working at" : "Worked at";
    return { title: "Big Tech", description: `${verb} ${exp.company}` };
  },

  [HighlightType.EX_FAANG]: (p) => {
    const exp = p.experience.find((e) => matchesSet(e.company, FAANG));
    if (!exp) return null;
    const title = exp.is_current ? "FAANG" : "Ex-FAANG";
    const verb = exp.is_current ? "Working at" : "Worked at";
    return { title, description: `${verb} ${exp.company}` };
  },

  [HighlightType.BIG_4]: (p) => {
    const exp = p.experience.find((e) => matchesSet(e.company, BIG_4));
    if (!exp) return null;
    const verb = exp.is_current ? "Working at" : "Worked at";
    return { title: "Big 4", description: `${verb} ${exp.company}` };
  },

  [HighlightType.TOP_INVESTMENT_BANK]: (p) => {
    const exp = p.experience.find((e) => matchesSet(e.company, TOP_INVESTMENT_BANKS));
    if (!exp) return null;
    const verb = exp.is_current ? "Working at" : "Worked at";
    return { title: "Top Investment Bank", description: `${verb} ${exp.company}` };
  },

  [HighlightType.TOP_CONSULTING_FIRM]: (p) => {
    const exp = p.experience.find((e) => matchesSet(e.company, TOP_CONSULTING_FIRMS));
    if (!exp) return null;
    const verb = exp.is_current ? "Working at" : "Worked at";
    return { title: "Top Consulting Firm", description: `${verb} ${exp.company}` };
  },

  [HighlightType.BOARD_MEMBER]: (p) => {
    const exp = p.experience.find((e) => norm(e.title).includes("board member"));
    if (!exp) return null;
    return { title: "Board Member", description: `Board Member at ${exp.company || "a company"}` };
  },

  [HighlightType.PROMOTED_TWICE]: (p) => {
    const exps = p.experience;
    if (exps.length < 3) return null;
    for (let i = 0; i < exps.length - 2; i++) {
      const c1 = norm(exps[i].company);
      const c2 = norm(exps[i + 1].company);
      const c3 = norm(exps[i + 2].company);
      if (c1 && c1 === c2 && c1 === c3) {
        return { title: "Promoted Twice", description: `Promoted twice at ${exps[i].company}` };
      }
    }
    return null;
  },

  [HighlightType.LONG_TENURE]: (p) => {
    let longest: { company: string; months: number } | null = null;
    const grouped = new Map<string, { company: string; totalMonths: number }>();

    for (const exp of p.experience) {
      const key = norm(exp.company);
      if (!key) continue;
      const m = monthsBetween(exp.start_date, exp.is_current ? undefined : exp.end_date);
      const existing = grouped.get(key);
      if (existing) {
        existing.totalMonths += m;
      } else {
        grouped.set(key, { company: exp.company, totalMonths: m });
      }
    }

    for (const entry of grouped.values()) {
      if (entry.totalMonths > 24 && (!longest || entry.totalMonths > longest.months)) {
        longest = { company: entry.company, months: entry.totalMonths };
      }
    }

    if (!longest) return null;
    return {
      title: "Long Tenure",
      description: `Worked at ${longest.company} for ${formatMonthsDuration(longest.months)}`,
    };
  },

  [HighlightType.PHD_HOLDER]: (p) => {
    const edu = p.education.find((e) => {
      const deg = norm(e.degree);
      return deg.includes("phd") || deg.includes("ph.d") || deg.includes("doctorate") || deg.includes("doctor of");
    });
    if (!edu) return null;
    return { title: "PhD Holder", description: `PhD at ${edu.school || "a university"}` };
  },
  [HighlightType.PATENT]: (p) => {
    const allText = [
      ...p.experience.map((e) => e.title),
      p.headline ?? "",
      p.summary ?? "",
      p.ai_summary ?? "",
    ].join(" ").toLowerCase();
    const found = PATENT_KEYWORDS.some((kw) => allText.includes(kw));
    if (!found) return null;
    return { title: "Patent Holder", description: "Patent pending or patent holder" };
  },

  [HighlightType.FULLSTACK]: (p) => {
    const titles = p.experience.map((e) => norm(e.title));
    const hasTitle = titles.some((t) => t.includes("fullstack") || t.includes("full stack") || t.includes("full-stack"));
    if (hasTitle) return { title: "Fullstack", description: "Full-stack development experience" };

    const skillsLow = (p.skills ?? []).map((s) => s.toLowerCase());
    const allText = [...titles, ...skillsLow, ...(p.ai_summary ? [norm(p.ai_summary)] : [])].join(" ");
    const hasFe = FRONTEND_TECHS.some((t) => allText.includes(t));
    const hasBe = BACKEND_TECHS.some((t) => allText.includes(t));
    if (hasFe && hasBe) return { title: "Fullstack", description: "Frontend and backend experience" };
    return null;
  },

  [HighlightType.WEB3]: (p) => {
    const allText = [
      ...p.experience.map((e) => `${e.title} ${e.company}`),
      ...(p.skills ?? []),
      p.headline ?? "",
      p.summary ?? "",
      p.ai_summary ?? "",
    ].join(" ").toLowerCase();
    const match = WEB_3_KEYWORDS.some((kw) => {
      const re = new RegExp(`\\b${kw.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\b`);
      return re.test(allText);
    });
    if (!match) return null;
    return { title: "Web3", description: "Experience with Web3 technologies" };
  },

  [HighlightType.TOP_10_US_UNI]: (p) => {
    const edu = p.education.find((e) => schoolMatchesSet(e.school, TOP_10_US_UNI));
    if (!edu) return null;
    return { title: "Top 10 US Uni", description: `Graduated from ${edu.school}` };
  },

  [HighlightType.TOP_20_US_UNI]: (p) => {
    const edu = p.education.find((e) => schoolMatchesSet(e.school, TOP_20_US_UNI));
    if (!edu) return null;
    return { title: "Top 20 US Uni", description: `Graduated from ${edu.school}` };
  },

  [HighlightType.TOP_50_US_UNI]: (p) => {
    const edu = p.education.find((e) => schoolMatchesSet(e.school, TOP_50_US_UNI));
    if (!edu) return null;
    return { title: "Top 50 US Uni", description: `Graduated from ${edu.school}` };
  },

  [HighlightType.TOP_100_GLOBAL_UNI]: (p) => {
    const edu = p.education.find((e) => schoolMatchesSet(e.school, TOP_100_GLOBAL_UNI));
    if (!edu) return null;
    return { title: "Top 100 Global Uni", description: `Graduated from ${edu.school}` };
  },

  [HighlightType.HBCU]: (p) => {
    const edu = p.education.find((e) => schoolMatchesSet(e.school, HBCU));
    if (!edu) return null;
    return { title: "HBCU", description: `Graduated from ${edu.school}` };
  },

  [HighlightType.TOP_MBA]: (p) => {
    const matches = p.education.filter((e) => {
      if (!schoolMatchesSet(e.school, TOP_MBA_PROGRAMS)) return false;
      if (norm(e.school).includes("online")) return false;
      const deg = norm(e.degree);
      const fld = norm(e.field);
      const combined = `${deg} ${fld}`;
      return (
        combined.includes("mba") ||
        combined.includes("master of business administration") ||
        (deg.includes("master") && (fld.includes("business") || fld.includes("management")))
      );
    });
    if (!matches.length) return null;
    const schools = matches.map((e) => e.school);
    return { title: "Top MBA", description: `MBA at ${schools.join(" and ")}` };
  },
};

// ---------------------------------------------------------------------------
// Main detection function
// ---------------------------------------------------------------------------

const PROFILE_VIEW_ONLY = new Set<HighlightType>([
  HighlightType.LONG_TENURE,
]);

export function detectHighlights(profile: ProfileData): HighlightResult[] {
  const active: HighlightResult[] = [];

  for (const type of Object.values(HighlightType)) {
    if (PROFILE_VIEW_ONLY.has(type)) continue;
    const detector = detectors[type];
    if (!detector) continue;
    const frame = HIGHLIGHT_FRAMES[type];
    const result = detector(profile);
    if (result) {
      active.push({
        type,
        title: result.title,
        description: result.description,
        icon: frame.icon,
        color: frame.color,
        gradientRgb: frame.gradientRgb,
        lightColor: frame.lightColor,
        lightGradientRgb: frame.lightGradientRgb,
      });
    }
  }

  const activeTypes = new Set(active.map((h) => h.type));
  const excludedTypes = new Set<HighlightType>();
  for (const h of active) {
    const frame = HIGHLIGHT_FRAMES[h.type];
    if (frame.excludes) {
      for (const ex of frame.excludes) {
        if (activeTypes.has(ex)) excludedTypes.add(ex);
      }
    }
  }

  return active.filter((h) => !excludedTypes.has(h.type));
}
